import { useEffect, useMemo, useRef, useState } from "react";
import {
  archiveOrganizerEvent,
  createOrganizerEvent,
  createPromoCode,
  createTicketTier,
  deactivatePromoCode,
  deactivateTicketTier,
  getScannerHistory,
  getOrganizerEvent,
  listOrganizerEvents,
  listOrganizerOrders,
  listOrganizerTickets,
  listOwnerOrganizations,
  publishOrganizerEvent,
  reactivatePromoCode,
  reassignOwnerEventOrganization,
  resendOrganizerOrder,
  resendOrganizerTicket,
  updateOrganizerEvent,
  updatePromoCode,
  updateTicketTier,
  uploadOrganizerEventFlyer
} from "../lib/api";
import type {
  EventFormInput,
  AuthMembership,
  OrganizerEventDetail,
  OrganizerEventSummary,
  OrganizerOrderSummary,
  OrganizerTicketSummary,
  OwnerOrganizationSummary,
  PromoCode,
  PromoCodeFormInput,
  ScannerStats,
  TicketTier,
  TicketTierFormInput
} from "../lib/types";
import type { MetricCardItem, ToastState, ToastType } from "../components/admin-ui";
import { FormField, MetricCardGroup, Panel, scheduleToastDismissal, StatusBadge, Toast } from "../components/admin-ui";
import WorkspaceSwitcher from "../components/WorkspaceSwitcher";
import type { WorkspaceNavigationAccess } from "../lib/workspace-navigation";
import { FLYER_INPUT_MAX_BYTES, optimizeFlyerImage } from "../lib/flyer-upload";
import { formatMoney, formatPriceInput, getTierPublishReadiness, parsePriceInput, validateStripeCheckoutLink } from "../lib/public-marketplace";
import { formatZonedDateTime, isoToZonedDateTimeInput, zonedDateTimeToIso } from "../lib/timezone";

interface OrganizerDashboardProps {
  navigate: (path: string) => void;
  path: string;
  workspaceNavigationAccess: WorkspaceNavigationAccess;
}

const currency = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const emptyEvent: EventFormInput = {
  title: "",
  slug: "",
  description: "",
  venueName: "",
  venueAddress: "",
  city: "",
  region: "",
  country: "US",
  timezone: "America/New_York",
  startsAt: new Date(Date.now() + 1000 * 60 * 60 * 24 * 30).toISOString(),
  endsAt: "",
  doorsAt: "",
  dressCode: "",
  ageRequirement: "",
  capacity: null,
  flyerImageUrl: "",
  salesStatus: "on_sale",
  showInPublicListings: true,
  visibility: "unlisted",
  status: "draft"
};

export type TierFormState = TicketTierFormInput & {
  closeOnDate: boolean;
  closeDate: string;
  closeTime: string;
};

const emptyTier: TierFormState = {
  name: "General Admission",
  description: "",
  priceCents: 0,
  currency: "USD",
  checkoutUrl: "",
  inventoryLimit: null,
  perOrderLimit: null,
  saleStartsAt: "",
  saleEndsAt: null,
  status: "active",
  salesStatus: "on_sale",
  sortOrder: 0,
  closeOnDate: false,
  closeDate: "",
  closeTime: ""
};

const emptyPromo: PromoCodeFormInput = {
  code: "",
  discountType: "percent",
  discountValue: 10,
  maxUses: null,
  startsAt: "",
  endsAt: "",
  isActive: true
};

function humanizeSalesStatus(value: "on_sale" | "sold_out" | "sales_paused"): string {
  if (value === "on_sale") return "On sale";
  if (value === "sales_paused") return "Sales paused";
  return "Sold out";
}

function humanizeVisibility(value: "private" | "unlisted" | "public"): string {
  if (value === "public") return "Public";
  if (value === "unlisted") return "Unlisted";
  return "Private";
}

function humanizeLifecycleStatus(value: EventFormInput["status"]): string {
  if (!value) return "Draft";
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function routeEventId(path: string): string | undefined {
  const parts = path.split("/").filter(Boolean);
  return parts[0] === "app" && parts[1] === "events" && parts[2] && parts[2] !== "new" ? parts[2] : undefined;
}

function toEventInput(detail: OrganizerEventDetail): EventFormInput {
  const event = detail.event;
  return {
    title: event.title,
    slug: event.slug,
    description: event.description ?? "",
    venueName: event.venueName ?? "",
    venueAddress: event.venueAddress ?? "",
    city: event.city ?? "",
    region: event.region ?? "",
    country: event.country ?? "US",
    timezone: event.timezone,
    startsAt: event.startsAt,
    endsAt: event.endsAt ?? "",
    doorsAt: event.doorsAt ?? "",
    dressCode: event.dressCode ?? "",
    ageRequirement: event.ageRequirement ?? "",
    capacity: event.capacity ?? null,
    flyerImageUrl: event.flyerImageUrl ?? "",
    salesStatus: event.salesStatus,
    showInPublicListings: event.showInPublicListings ?? true,
    visibility: event.visibility,
    status: event.status
  };
}

function toDateTimeLocal(value?: string): string {
  if (!value) return "";
  const date = new Date(value);
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 16);
}

function fromDateTimeLocal(value: string): string {
  if (!value) return "";
  return new Date(value).toISOString();
}

function tierInput(tier: TicketTier, timeZone: string): TierFormState {
  const close = isoToZonedDateTimeInput(tier.saleEndsAt, timeZone);
  return {
    name: tier.name,
    description: tier.description ?? "",
    priceCents: tier.priceCents,
    currency: tier.currency,
    checkoutUrl: tier.checkoutUrl ?? "",
    inventoryLimit: tier.inventoryLimit ?? null,
    perOrderLimit: tier.perOrderLimit ?? null,
    saleStartsAt: tier.saleStartsAt ?? "",
    saleEndsAt: tier.saleEndsAt ?? null,
    status: tier.status,
    salesStatus: tier.salesStatus,
    sortOrder: tier.sortOrder,
    closeOnDate: Boolean(tier.saleEndsAt),
    closeDate: close.date,
    closeTime: close.time
  };
}

function tierPayload(value: TierFormState): TicketTierFormInput {
  const { closeOnDate, closeDate, closeTime, ...payload } = value;
  return { ...payload, saleEndsAt: closeOnDate ? payload.saleEndsAt ?? null : null };
}

function tierScheduleIssues(tier: TierFormState, eventEndsAt: string | undefined, isEditing: boolean): string[] {
  if (!tier.closeOnDate) return [];
  if (!tier.closeDate || !tier.closeTime || !tier.saleEndsAt) return ["Enter a valid close date and time in the event timezone."];
  const closesAt = Date.parse(tier.saleEndsAt);
  if (Number.isNaN(closesAt)) return ["Enter a valid close date and time in the event timezone."];
  if (eventEndsAt && closesAt > Date.parse(eventEndsAt)) return ["Scheduled close must be on or before the event end time."];
  if (!isEditing && closesAt <= Date.now()) return ["Scheduled close must be in the future when creating a tier."];
  return [];
}

function promoInput(promo: PromoCode): PromoCodeFormInput {
  return {
    code: promo.code,
    discountType: promo.discountType === "amount_cents" ? "fixed" : "percent",
    discountValue: promo.discountValue,
    maxUses: promo.maxRedemptions ?? null,
    startsAt: promo.startsAt ?? "",
    endsAt: promo.endsAt ?? "",
    isActive: promo.isActive
  };
}

export type PromoAvailability = "available" | "scheduled" | "expired" | "exhausted" | "inactive";

export function promoAvailability(promo: PromoCode, now = Date.now()): PromoAvailability {
  if (!promo.isActive) return "inactive";
  if (promo.startsAt && Date.parse(promo.startsAt) > now) return "scheduled";
  if (promo.endsAt && Date.parse(promo.endsAt) <= now) return "expired";
  if (promo.maxRedemptions !== undefined && promo.redeemedCount >= promo.maxRedemptions) return "exhausted";
  return "available";
}

function promoAvailabilityLabel(promo: PromoCode): string {
  const availability = promoAvailability(promo);
  if (availability === "available") return "Available";
  if (availability === "scheduled") return "Scheduled";
  if (availability === "expired") return "Expired";
  if (availability === "exhausted") return "Usage limit reached";
  return "Inactive";
}

export function doorOperationMetricItems(scanStats?: ScannerStats): MetricCardItem[] {
  return [
    { label: "Valid tickets", value: String(scanStats?.validTickets ?? 0) },
    { label: "Checked in now", value: String(scanStats?.checkedInNow ?? 0) },
    { label: "Check-outs", value: String(scanStats?.checkedOutCount ?? 0) },
    { label: "Invalid scans", value: String(scanStats?.invalidScanCount ?? 0) }
  ];
}

export function nextExpandedTierId(current: string | undefined, tierId: string): string | undefined {
  return current === tierId ? undefined : tierId;
}

export function HostOrganizationControl({
  isPlatformOwner,
  eventTitle,
  currentOrganizerId,
  currentOrganizerName,
  organizations,
  selectedOrganizerId,
  onSelect,
  onSave,
  isBusy
}: {
  isPlatformOwner: boolean;
  eventTitle: string;
  currentOrganizerId: string;
  currentOrganizerName: string;
  organizations: OwnerOrganizationSummary[];
  selectedOrganizerId: string;
  onSelect: (organizerId: string) => void;
  onSave: () => void;
  isBusy: boolean;
}) {
  if (!isPlatformOwner) {
    return <FormField label="Host Organization"><p className="form-field__hint">{currentOrganizerName}</p></FormField>;
  }

  const changed = selectedOrganizerId && selectedOrganizerId !== currentOrganizerId;
  return (
    <FormField label="Host Organization" hint="Only platform owners can change the organization hosting an existing event.">
      <select value={selectedOrganizerId} onChange={(event) => onSelect(event.target.value)} disabled={isBusy}>
        {organizations.filter((organization) => organization.isActive).map((organization) => <option key={organization.id} value={organization.id}>{organization.name}</option>)}
      </select>
      <button className="secondary-action" type="button" disabled={isBusy || !changed} onClick={onSave}>
        Save Host Organization
      </button>
      {changed && <p className="form-field__hint">Move &ldquo;{eventTitle}&rdquo; from {currentOrganizerName} to {organizations.find((organization) => organization.id === selectedOrganizerId)?.name ?? "the selected organization"}?</p>}
    </FormField>
  );
}

export default function OrganizerDashboard({ navigate, path, workspaceNavigationAccess }: OrganizerDashboardProps) {
  const [events, setEvents] = useState<OrganizerEventSummary[]>([]);
  const [memberships, setMemberships] = useState<AuthMembership[]>([]);
  const [hostOrganizations, setHostOrganizations] = useState<OwnerOrganizationSummary[]>([]);
  const [selectedHostOrganizationId, setSelectedHostOrganizationId] = useState("");
  const [detail, setDetail] = useState<OrganizerEventDetail>();
  const [eventForm, setEventForm] = useState<EventFormInput>(emptyEvent);
  const [newTierForm, setNewTierForm] = useState<TierFormState>(emptyTier);
  const [isNewTierFormOpen, setIsNewTierFormOpen] = useState(false);
  const [expandedTierId, setExpandedTierId] = useState<string | undefined>();
  const [editingTierForm, setEditingTierForm] = useState<TierFormState>(emptyTier);
  const [promoForm, setPromoForm] = useState<PromoCodeFormInput>(emptyPromo);
  const [expandedPromoId, setExpandedPromoId] = useState<string | undefined>();
  const [editingPromoForm, setEditingPromoForm] = useState<PromoCodeFormInput>(emptyPromo);
  const [orders, setOrders] = useState<OrganizerOrderSummary[]>([]);
  const [tickets, setTickets] = useState<OrganizerTicketSummary[]>([]);
  const [scanStats, setScanStats] = useState<ScannerStats>();
  const [toast, setToast] = useState<ToastState>();
  const [pendingFlyerMessage, setPendingFlyerMessage] = useState("");
  const [pendingFlyerFile, setPendingFlyerFile] = useState<File>();
  const [isDraggingFlyer, setIsDraggingFlyer] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [isUploadingFlyer, setIsUploadingFlyer] = useState(false);
  const flyerInputRef = useRef<HTMLInputElement>(null);
  const toastIdRef = useRef(0);

  const eventId = useMemo(() => routeEventId(path), [path]);
  const isNew = path === "/app/events/new";
  const isEventsList = path === "/app/events";

  async function refresh() {
    const eventList = await listOrganizerEvents();
    setEvents(eventList.events);
    const eligibleMemberships = eventList.memberships.filter((membership) => membership.role === "owner" || membership.role === "manager");
    setMemberships(eligibleMemberships);
    if (eligibleMemberships.length === 1) {
      setEventForm((current) => current.organizerId ? current : { ...current, organizerId: eligibleMemberships[0].organizerId });
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  useEffect(() => {
    if (!workspaceNavigationAccess.isOwner) return;
    void listOwnerOrganizations().then(setHostOrganizations).catch(() => setHostOrganizations([]));
  }, [workspaceNavigationAccess.isOwner]);

  useEffect(() => {
    setToast(undefined);
    if (isNew) {
      setDetail(undefined);
      setEventForm({ ...emptyEvent, organizerId: memberships.length === 1 ? memberships[0].organizerId : undefined });
      setNewTierForm(emptyTier);
      setIsNewTierFormOpen(false);
      setExpandedTierId(undefined);
      setEditingTierForm(emptyTier);
      setExpandedPromoId(undefined);
      setEditingPromoForm(emptyPromo);
      setPendingFlyerFile(undefined);
      setPendingFlyerMessage("");
      return;
    }
    if (eventId) {
      getOrganizerEvent(eventId).then((item) => {
        if (!item) return;
        setDetail(item);
        setEventForm(toEventInput(item));
        setSelectedHostOrganizationId(item.event.organizerId);
        setExpandedTierId(undefined);
        setIsNewTierFormOpen(false);
        setEditingTierForm(emptyTier);
      });
      Promise.all([listOrganizerOrders(eventId), listOrganizerTickets(eventId)]).then(([nextOrders, nextTickets]) => {
        setOrders(nextOrders);
        setTickets(nextTickets);
      });
      getScannerHistory(eventId).then((next) => setScanStats(next.stats));
    }
  }, [eventId, isNew]);

  function showToast(message: string, type: ToastType) {
    setToast({ id: ++toastIdRef.current, message, type });
  }

  useEffect(() => {
    if (!toast) return;
    return scheduleToastDismissal(
      () => setToast((current) => current?.id === toast.id ? { ...current, leaving: true } : current),
      () => setToast((current) => current?.id === toast.id ? undefined : current)
    );
  }, [toast?.id]);

  const hasActiveTier = Boolean(detail?.ticketTiers.some((tier) => tier.status !== "hidden"));
  const flyerPreviewUrl = eventForm.flyerImageUrl?.trim();
  const newTierValidationMessages = [
    ...getTierPublishReadiness(newTierForm),
    ...tierScheduleIssues(newTierForm, detail?.event.endsAt, false)
  ];
  const publishReadiness = detail
    ? [
        ...(detail.ticketTiers.length === 0 ? ["At least one ticket tier is required."] : []),
        ...detail.ticketTiers.flatMap((tier) => getTierPublishReadiness(tierInput(tier, detail.event.timezone)))
      ]
    : [];

  async function run(action: () => Promise<void>, success: string) {
    setIsBusy(true);
    try {
      await action();
      await refresh();
      showToast(success, "success");
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Action failed.", "error");
    } finally {
      setIsBusy(false);
    }
  }

  async function refreshEventOrdersAndTickets() {
    if (!eventId) return;
    const [nextOrders, nextTickets] = await Promise.all([listOrganizerOrders(eventId), listOrganizerTickets(eventId)]);
    setOrders(nextOrders);
    setTickets(nextTickets);
    setScanStats((await getScannerHistory(eventId)).stats);
  }

  async function submitEvent() {
    if (isNew && newTierValidationMessages.length > 0) {
      return;
    }
    const success = isNew && pendingFlyerFile ? "Event created and flyer uploaded." : isNew ? "Event created." : "Event updated.";
    await run(async () => {
      if (isNew) {
        const created = await createOrganizerEvent(eventForm);
        const firstTier = created.ticketTiers[0];
        const createdWithTier = firstTier ? await updateTicketTier(firstTier.id, { ...tierPayload(newTierForm), sortOrder: 0 }) : created;
        let nextDetail = createdWithTier;
        if (pendingFlyerFile) {
          const optimized = await optimizeFlyerImage(pendingFlyerFile);
          const uploaded = await uploadOrganizerEventFlyer(created.event.id, optimized.file);
          const refreshed = await getOrganizerEvent(created.event.id);
          if (refreshed) nextDetail = refreshed;
          setEventForm((current) => ({ ...current, flyerImageUrl: uploaded.url }));
          setPendingFlyerFile(undefined);
        }
        setDetail(nextDetail);
        navigate(`/app/events/${created.event.id}`);
      } else if (eventId) {
        const updated = await updateOrganizerEvent(eventId, eventForm);
        setDetail(updated);
        setEventForm(toEventInput(updated));
      }
    }, success);
  }

  async function refreshCurrentEvent(id: string) {
    const updated = await getOrganizerEvent(id);
    if (!updated) return;
    setDetail(updated);
    setEventForm(toEventInput(updated));
    setSelectedHostOrganizationId(updated.event.organizerId);
  }

  async function saveHostOrganization() {
    if (!detail || !eventId || selectedHostOrganizationId === detail.event.organizerId) return;
    const nextOrganization = hostOrganizations.find((organization) => organization.id === selectedHostOrganizationId);
    if (!nextOrganization) return;
    if (!window.confirm(`Move "${detail.event.title}" from ${detail.event.hostedBy?.[0] ?? detail.event.organizerId} to ${nextOrganization.name}?`)) return;
    await run(async () => {
      const updated = await reassignOwnerEventOrganization(eventId, selectedHostOrganizationId);
      setDetail(updated);
      setEventForm(toEventInput(updated));
      setSelectedHostOrganizationId(updated.event.organizerId);
    }, "Host organization updated.");
  }

  async function uploadFlyer(file?: File) {
    if (!file) return;
    setPendingFlyerMessage("");
    if (!eventId) {
      try {
        // Validate early, but wait for a real event id before uploading to R2.
        await optimizeFlyerImage(file);
        setPendingFlyerFile(file);
        setPendingFlyerMessage("Flyer selected. Create the draft and AnTix will upload it automatically.");
      } catch (error) {
        showToast(error instanceof Error ? error.message : "Flyer could not be prepared.", "error");
      } finally {
        if (flyerInputRef.current) flyerInputRef.current.value = "";
      }
      return;
    }
    setIsUploadingFlyer(true);
    try {
      const optimized = await optimizeFlyerImage(file);
      const uploaded = await uploadOrganizerEventFlyer(eventId, optimized.file);
      setEventForm((current) => ({ ...current, flyerImageUrl: uploaded.url }));
      await refreshCurrentEvent(eventId);
      showToast(`Flyer uploaded (${optimized.width} x ${optimized.height}).`, "success");
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Flyer upload failed.", "error");
    } finally {
      setIsUploadingFlyer(false);
      if (flyerInputRef.current) flyerInputRef.current.value = "";
    }
  }

  async function clearFlyer() {
    if (!eventId) {
      setEventForm((current) => ({ ...current, flyerImageUrl: "" }));
      setPendingFlyerFile(undefined);
      setPendingFlyerMessage("");
      showToast("Pending flyer removed.", "info");
      return;
    }

    await run(async () => {
      const updated = await updateOrganizerEvent(eventId, { ...eventForm, flyerImageUrl: "" });
      setDetail(updated);
      setEventForm(toEventInput(updated));
    }, "Flyer removed from event.");
  }

  function editTier(tier: TicketTier) {
    const nextExpandedId = nextExpandedTierId(expandedTierId, tier.id);
    if (!nextExpandedId) {
      setExpandedTierId(undefined);
      return;
    }
    setIsNewTierFormOpen(false);
    setExpandedTierId(nextExpandedId);
    setEditingTierForm(tierInput(tier, detail?.event.timezone ?? eventForm.timezone ?? "America/New_York"));
  }

  function openNewTierForm() {
    setExpandedTierId(undefined);
    setEditingTierForm(emptyTier);
    setNewTierForm({ ...emptyTier, sortOrder: detail?.ticketTiers.length ?? 0 });
    setIsNewTierFormOpen(true);
  }

  function cancelNewTierForm() {
    setIsNewTierFormOpen(false);
    setNewTierForm({ ...emptyTier, sortOrder: detail?.ticketTiers.length ?? 0 });
  }

  async function saveTier(tier: TicketTier) {
    if (!detail) return;
    const validationMessages = [
      ...getTierPublishReadiness(editingTierForm),
      ...tierScheduleIssues(editingTierForm, detail.event.endsAt, true)
    ];
    if (validationMessages.length > 0) {
      return;
    }
    await run(async () => {
      const next = await updateTicketTier(tier.id, tierPayload(editingTierForm));
      setDetail(next);
      const savedTier = next.ticketTiers.find((item) => item.id === tier.id);
      if (savedTier) setEditingTierForm(tierInput(savedTier, next.event.timezone));
      setExpandedTierId(tier.id);
    }, "Ticket tier updated.");
  }

  async function createNewTier() {
    if (!detail || newTierValidationMessages.length > 0) return;
    await run(async () => {
      const next = await createTicketTier(detail.event.id, tierPayload(newTierForm));
      setDetail(next);
      setNewTierForm({ ...emptyTier, sortOrder: next.ticketTiers.length });
      setIsNewTierFormOpen(false);
    }, "Ticket tier added.");
  }

  async function deleteTier(tier: TicketTier) {
    if (!detail) return;
    await run(async () => {
      await deactivateTicketTier(tier.id);
      setDetail(await getOrganizerEvent(detail.event.id));
      setExpandedTierId((current) => current === tier.id ? undefined : current);
    }, "Tier deactivated.");
  }

  async function savePromo(promo?: PromoCode) {
    if (!detail) return;
    await run(async () => {
      const next = promo
        ? await updatePromoCode(promo.id, promoForm)
        : await createPromoCode(detail.event.id, promoForm);
      setDetail(next);
      setPromoForm(emptyPromo);
      setExpandedPromoId(undefined);
      setEditingPromoForm(emptyPromo);
    }, promo ? "Promo code updated." : "Promo code added.");
  }

  function editPromo(promo: PromoCode) {
    setExpandedPromoId((current) => current === promo.id ? undefined : promo.id);
    setEditingPromoForm(promoInput(promo));
  }

  async function reactivatePromo(promo: PromoCode) {
    if (!detail) return;
    await run(async () => {
      const next = await reactivatePromoCode(promo.id, promoInput(promo));
      setDetail(next);
    }, "Promo code reactivated.");
  }

  async function resendOrder(orderId: string) {
    await run(async () => {
      await resendOrganizerOrder(orderId);
      await refreshEventOrdersAndTickets();
    }, "Ticket email resent.");
  }

  async function resendTicket(ticketId: string) {
    await run(async () => {
      await resendOrganizerTicket(ticketId);
      await refreshEventOrdersAndTickets();
    }, "Ticket email resent.");
  }

  const eventRows = events.map((event) => (
    <div className="table-row management-row" key={event.id}>
      <button className="text-link" data-label="Event" type="button" onClick={() => navigate(`/app/events/${event.id}`)}>
        {event.title}
      </button>
      <span data-label="Status" className={`pill status-${event.status}`}>{event.status}</span>
      <span data-label="Visibility">{event.visibility}</span>
      <span data-label="Date">{new Date(event.startsAt).toLocaleDateString()}</span>
      <span data-label="Tiers">{event.ticketTierCount}</span>
    </div>
  ));

  return (
    <section className="page dashboard-page">
      <div className="page-title-row page-title-stack">
        <div>
          <div className="eyebrow">Organizer workspace</div>
          <h1>{isNew ? "Create event" : eventId ? "Manage event" : "Event Manager"}</h1>
          <p>
            Build the event as a draft first, keep visibility unlisted until checkout and scanner rehearsal pass, then publish.
          </p>
        </div>
        <WorkspaceSwitcher path={path} access={workspaceNavigationAccess} navigate={navigate} />
      </div>

      {isEventsList && (
        <>
          <Panel title="Events" description={`${events.length} managed across this organizer workspace.`} actions={
            <button className="primary-action" type="button" onClick={() => navigate("/app/events/new")}>Create event</button>
          }>
            <div className="table-shell management-table">
              <div className="table-row table-head management-row">
                <span>Event</span>
                <span>Status</span>
                <span>Visibility</span>
                <span>Date</span>
                <span>Tiers</span>
              </div>
              {eventRows}
            </div>
          </Panel>
        </>
      )}

      {(isNew || eventId) && (
        <div className="management-grid">
          <Panel
            title="Event details"
            description="Keep lifecycle status, public visibility, and sales status aligned: lifecycle controls where the event is in its run, visibility controls who can discover it, and sales status controls whether tickets can be purchased."
            actions={
              detail?.event.status === "live" ? (
                <button className="secondary-action" onClick={() => navigate(`/e/${detail.event.slug}`)}>Public page</button>
              ) : undefined
            }
          >
            <div className="form-grid">
              {!isNew && detail && <HostOrganizationControl
                isPlatformOwner={workspaceNavigationAccess.isOwner}
                eventTitle={detail.event.title}
                currentOrganizerId={detail.event.organizerId}
                currentOrganizerName={detail.event.hostedBy?.[0] ?? detail.event.organizerId}
                organizations={hostOrganizations}
                selectedOrganizerId={selectedHostOrganizationId || detail.event.organizerId}
                onSelect={setSelectedHostOrganizationId}
                onSave={() => void saveHostOrganization()}
                isBusy={isBusy}
              />}
              {isNew && memberships.length > 1 && <FormField label="Host Organization" hint="The selected organization becomes the public event host.">
                <select value={eventForm.organizerId ?? ""} onChange={(e) => setEventForm({ ...eventForm, organizerId: e.target.value || undefined })}>
                  <option value="">Select organization</option>
                  {memberships.map((membership) => <option key={membership.organizerId} value={membership.organizerId}>{membership.organizerName ?? membership.organizerId}</option>)}
                </select>
              </FormField>}
              <FormField label="Title">
                <input value={eventForm.title} onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })} />
              </FormField>
              <FormField label="Slug" hint="Leave blank and AnTix will generate one from the title.">
                <input value={eventForm.slug ?? ""} onChange={(e) => setEventForm({ ...eventForm, slug: e.target.value })} />
              </FormField>
              <FormField label="Starts at">
                <input
                  type="datetime-local"
                  value={toDateTimeLocal(eventForm.startsAt)}
                  onChange={(e) => setEventForm({ ...eventForm, startsAt: fromDateTimeLocal(e.target.value) })}
                />
              </FormField>
              <FormField label="Ends at">
                <input
                  type="datetime-local"
                  value={toDateTimeLocal(eventForm.endsAt)}
                  onChange={(e) => setEventForm({ ...eventForm, endsAt: fromDateTimeLocal(e.target.value) })}
                />
              </FormField>
              <FormField label="Venue">
                <input value={eventForm.venueName ?? ""} onChange={(e) => setEventForm({ ...eventForm, venueName: e.target.value })} />
              </FormField>
              <FormField label="Venue address">
                <input value={eventForm.venueAddress ?? ""} onChange={(e) => setEventForm({ ...eventForm, venueAddress: e.target.value })} />
              </FormField>
              <FormField label="City">
                <input value={eventForm.city ?? ""} onChange={(e) => setEventForm({ ...eventForm, city: e.target.value })} />
              </FormField>
              <FormField label="Region">
                <input value={eventForm.region ?? ""} onChange={(e) => setEventForm({ ...eventForm, region: e.target.value })} />
              </FormField>
              <FormField label="Capacity">
                <input
                  type="number"
                  min="1"
                  value={eventForm.capacity ?? ""}
                  onChange={(e) => setEventForm({ ...eventForm, capacity: e.target.value ? Number(e.target.value) : null })}
                />
              </FormField>
              <FormField label="Age requirement" hint="Examples: 18+, 21+, or All ages.">
                <input value={eventForm.ageRequirement ?? ""} onChange={(e) => setEventForm({ ...eventForm, ageRequirement: e.target.value })} />
              </FormField>
              <FormField label="Dress code">
                <input value={eventForm.dressCode ?? ""} onChange={(e) => setEventForm({ ...eventForm, dressCode: e.target.value })} />
              </FormField>
              <FormField label="Lifecycle status" hint="Draft keeps the event private to your workspace. Live makes it eligible for public selling when visibility allows it.">
                <select value={eventForm.status} onChange={(e) => setEventForm({ ...eventForm, status: e.target.value as EventFormInput["status"] })}>
                  <option value="draft">Draft</option>
                  <option value="live">Live</option>
                  <option value="completed">Completed</option>
                  <option value="archived">Archived</option>
                </select>
              </FormField>
              <FormField label="Sales status" hint="Use sales paused to keep the event visible without allowing purchases.">
                <select value={eventForm.salesStatus} onChange={(e) => setEventForm({ ...eventForm, salesStatus: e.target.value as EventFormInput["salesStatus"] })}>
                  <option value="on_sale">On sale</option>
                  <option value="sold_out">Sold out</option>
                  <option value="sales_paused">Sales paused</option>
                </select>
              </FormField>
              <FormField label="Visibility" hint="Public appears in tickets. Unlisted is shareable by direct event link only.">
                <select value={eventForm.visibility} onChange={(e) => setEventForm({ ...eventForm, visibility: e.target.value as EventFormInput["visibility"] })}>
                  <option value="private">Private</option>
                  <option value="unlisted">Unlisted</option>
                  <option value="public">Public</option>
                </select>
              </FormField>
              <FormField label="Public listing" hint="Hide from marketplace lists while keeping the direct event page available when visibility permits.">
                <select
                  value={eventForm.showInPublicListings === false ? "hidden" : "listed"}
                  onChange={(e) => setEventForm({ ...eventForm, showInPublicListings: e.target.value === "listed" })}
                >
                  <option value="listed">Listed</option>
                  <option value="hidden">Hidden</option>
                </select>
              </FormField>
              <FormField label="Description" wide>
                <textarea value={eventForm.description ?? ""} onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })} />
              </FormField>
            </div>
            <div className="flyer-upload-section">
              <div>
                <span className="organizer-section-label">Upload event flyer</span>
                <p className="panel-copy">JPG, PNG, or WebP. Maximum {Math.round(FLYER_INPUT_MAX_BYTES / 1024 / 1024)} MB. The browser optimizes one WebP flyer before upload.</p>
              </div>
              <div
                className={`flyer-upload-zone${isDraggingFlyer ? " flyer-upload-zone--dragging" : ""}`}
                onDragEnter={(event) => {
                  event.preventDefault();
                  setIsDraggingFlyer(true);
                }}
                onDragOver={(event) => event.preventDefault()}
                onDragLeave={() => setIsDraggingFlyer(false)}
                onDrop={(event) => {
                  event.preventDefault();
                  setIsDraggingFlyer(false);
                  void uploadFlyer(event.dataTransfer.files[0]);
                }}
              >
                <input
                  ref={flyerInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  disabled={isUploadingFlyer}
                  onChange={(event) => void uploadFlyer(event.currentTarget.files?.[0])}
                />
                <strong>
                  {eventId
                    ? "Drop flyer here or choose a file"
                    : pendingFlyerFile
                      ? `Ready after draft: ${pendingFlyerFile.name}`
                      : "Choose a flyer now, upload after draft creation"}
                </strong>
                <span>
                  {isUploadingFlyer
                    ? "Optimizing and uploading..."
                    : eventId
                      ? "AnTix stores uploads under antix/events/... in the shared ACS assets bucket."
                      : "AnTix will not upload until the event draft has an ID."}
                </span>
                <button
                  className="secondary-action"
                  type="button"
                  disabled={isUploadingFlyer}
                  onClick={() => flyerInputRef.current?.click()}
                >
                  {flyerPreviewUrl || pendingFlyerFile ? "Replace image" : "Choose image"}
                </button>
              </div>
              {pendingFlyerMessage && <p className="form-success">{pendingFlyerMessage}</p>}
              <details className="flyer-url-advanced">
                <summary>Advanced: Use an existing image URL</summary>
                <FormField label="Flyer image URL" hint="Manual fallback for an already hosted image. Save the event after changing it.">
                  <input value={eventForm.flyerImageUrl ?? ""} onChange={(e) => setEventForm({ ...eventForm, flyerImageUrl: e.target.value })} />
                </FormField>
              </details>
            </div>
            {flyerPreviewUrl && (
              <div className="organizer-flyer-preview">
                <div>
                  <span className="organizer-section-label">Flyer preview</span>
                  <p className="panel-copy">This is how the flyer will anchor the public ticket listing.</p>
                </div>
                <img
                  src={flyerPreviewUrl}
                  alt={`${eventForm.title || "Event"} flyer preview`}
                  onError={(current) => {
                    current.currentTarget.style.display = "none";
                    current.currentTarget.nextElementSibling?.classList.add("organizer-flyer-preview__fallback-visible");
                  }}
                />
                <div className="organizer-flyer-preview__fallback">Image preview unavailable for this URL.</div>
                <button className="secondary-action" type="button" disabled={isBusy || isUploadingFlyer} onClick={() => void clearFlyer()}>
                  Remove/reset flyer
                </button>
              </div>
            )}
            {detail && (
              <div className="detail-row organizer-status-row">
                <StatusBadge label={`Lifecycle: ${humanizeLifecycleStatus(detail.event.status)}`} tone={detail.event.status === "live" ? "success" : "info"} />
                <StatusBadge label={`Visibility: ${humanizeVisibility(detail.event.visibility)}`} tone="info" />
                <StatusBadge label={`Sales: ${humanizeSalesStatus(detail.event.salesStatus)}`} tone={detail.event.salesStatus === "on_sale" ? "success" : "warning"} />
                <StatusBadge
                  label={hasActiveTier ? "Ready to publish" : "Add an active tier before publish"}
                  tone={hasActiveTier ? "success" : "warning"}
                />
              </div>
            )}
            {detail && publishReadiness.length > 0 && (
              <div className="organizer-readiness">
                <strong>Publish checklist</strong>
                <ul>
                  {publishReadiness.map((issue) => (
                    <li key={issue}>{issue}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="button-row">
              <button className="primary-action" disabled={isBusy} onClick={submitEvent}>{isNew ? "Create draft" : "Save event"}</button>
              {detail && <button className="secondary-action" disabled={isBusy || !hasActiveTier} onClick={() => run(async () => { await publishOrganizerEvent(detail.event.id); setDetail(await getOrganizerEvent(detail.event.id)); }, "Event published.")}>Publish</button>}
              {detail && <button className="secondary-action" disabled={isBusy} onClick={() => run(async () => { const updated = await updateOrganizerEvent(detail.event.id, { ...eventForm, status: "completed" }); setDetail(updated); setEventForm(toEventInput(updated)); }, "Event marked completed.")}>Mark completed</button>}
              {detail && <button className="secondary-action" disabled={isBusy} onClick={() => run(async () => { await archiveOrganizerEvent(detail.event.id); setDetail(await getOrganizerEvent(detail.event.id)); }, "Event archived.")}>Archive</button>}
            </div>
          </Panel>

          {(isNew || detail) && (
            <>
              <Panel
                title="Ticket tiers"
                description={
                  isNew
                    ? "Your first tier starts here. Creating the draft will persist this initial ticket tier even if checkout details are incomplete."
                    : `${detail?.ticketTiers.length ?? 0} configured. Each public tier needs a clear price and active sales status before you publish.`
                }
              >
                {isNew ? (
                  <>
                    <TierFields
                      value={newTierForm}
                      onChange={setNewTierForm}
                      timeZone={eventForm.timezone ?? "America/New_York"}
                    />
                    <TierValidationMessages messages={newTierValidationMessages} />
                  </>
                ) : detail && (
                  <>
                    <div className="button-row">
                      <button className="primary-action" type="button" disabled={isBusy || isNewTierFormOpen} onClick={openNewTierForm}>
                        + Create New Tier
                      </button>
                    </div>

                    {isNewTierFormOpen && (
                      <section className="tier-accordion-card tier-accordion-card--new" aria-labelledby="new-ticket-tier-heading">
                        <div className="tier-accordion__new-heading">
                          <div>
                            <h3 id="new-ticket-tier-heading">New Ticket Tier</h3>
                            <p>Set up a separate tier, then create it when the details are ready.</p>
                          </div>
                        </div>
                        <div className="tier-accordion__content">
                          <TierFields
                            value={newTierForm}
                            onChange={setNewTierForm}
                            timeZone={detail.event.timezone}
                          />
                          <TierValidationMessages messages={newTierValidationMessages} />
                          <div className="button-row">
                            <button className="primary-action" type="button" disabled={isBusy} onClick={() => void createNewTier()}>Create Tier</button>
                            <button className="secondary-action" type="button" disabled={isBusy} onClick={cancelNewTierForm}>Cancel</button>
                          </div>
                        </div>
                      </section>
                    )}

                    <div className="stack-list tier-accordion-list">
                      {detail.ticketTiers.map((tier) => {
                        const visibleTierCount = detail.ticketTiers.filter((item) => item.status !== "hidden").length;
                        const cannotRemoveLastLiveTier = detail.event.status === "live" && tier.status !== "hidden" && visibleTierCount <= 1;
                        const isExpanded = expandedTierId === tier.id;
                        const validationMessages = isExpanded
                          ? [
                              ...getTierPublishReadiness(editingTierForm),
                              ...tierScheduleIssues(editingTierForm, detail.event.endsAt, true)
                            ]
                          : [];
                        return (
                          <TierAccordionCard
                            key={tier.id}
                            tier={tier}
                            form={editingTierForm}
                            isExpanded={isExpanded}
                            timeZone={detail.event.timezone}
                            validationMessages={validationMessages}
                            isBusy={isBusy}
                            cannotDelete={cannotRemoveLastLiveTier}
                            onToggle={() => editTier(tier)}
                            onChange={setEditingTierForm}
                            onSave={() => void saveTier(tier)}
                            onDelete={() => {
                              if (!window.confirm(`Delete ${tier.name}? This removes the tier from organizer use and public listings.`)) return;
                              void deleteTier(tier);
                            }}
                          />
                        );
                      })}
                    </div>
                  </>
                )}
              </Panel>

              {detail && <Panel title="Promo codes" description={`${detail.promoCodes.length} codes available for this event.`}>
                <PromoFields value={promoForm} onChange={setPromoForm} />
                <button className="primary-action" disabled={isBusy} onClick={() => savePromo()}>Add promo</button>
                <div className="stack-list">
                  {detail.promoCodes.map((promo) => <PromoAccordionCard
                    key={promo.id}
                    promo={promo}
                    form={editingPromoForm}
                    isExpanded={expandedPromoId === promo.id}
                    isBusy={isBusy}
                    onToggle={() => editPromo(promo)}
                    onChange={setEditingPromoForm}
                    onSave={() => void savePromo(promo)}
                    onCancel={() => setExpandedPromoId(undefined)}
                    onDeactivate={() => void run(async () => { await deactivatePromoCode(promo.id); setDetail(await getOrganizerEvent(detail.event.id)); }, "Promo code deactivated.")}
                    onReactivate={() => void reactivatePromo(promo)}
                  />)}
                </div>
              </Panel>}

              {detail && <section className="panel">
                <div className="panel-header">
                  <h2>Door operations</h2>
                  <button className="secondary-action" onClick={() => navigate(`/scanner?eventId=${detail.event.id}`)}>Open scanner</button>
                </div>
                <MetricCardGroup items={doorOperationMetricItems(scanStats)} className="scanner-stats" />
                <div className="stack-list">
                  {(scanStats?.recentActivity.length ?? 0) === 0 && <p>No scan activity yet.</p>}
                  {scanStats?.recentActivity.slice(0, 5).map((entry) => (
                    <article className="mini-card" key={entry.id}>
                      <strong>{entry.result}</strong>
                      <span>{entry.action} / {entry.source ?? "manual"} / {new Date(entry.createdAt).toLocaleString()}</span>
                      <p>{entry.ticketCodeSnapshot ?? entry.scannedCode ?? "No code"}</p>
                    </article>
                  ))}
                </div>
              </section>}

              {detail && <section className="panel">
                <div className="panel-header"><h2>Orders</h2><span>{orders.length} recent</span></div>
                <div className="table-shell management-table">
                  <div className="table-row order-row table-head">
                    <span>Buyer</span><span>Phone</span><span>Marketing</span><span>Status</span><span>Qty</span><span>Total</span><span>Delivery</span><span>Action</span>
                  </div>
                  {orders.map((order) => (
                    <div className="table-row order-row" key={order.id}>
                      <span>{order.buyerName ?? order.buyerEmail}</span>
                      <span>{order.buyerPhone ?? "Not provided"}</span>
                      <span>
                        Email: {order.marketingEmailOptIn ? "Opted in" : "Not opted in"} / SMS: {order.marketingSmsOptIn ? "Opted in" : "Not opted in"}
                      </span>
                      <span>{order.status}</span>
                      <span>{order.ticketQuantity}</span>
                      <span>{currency.format(order.totalCents / 100)}</span>
                      <span>{order.ticketDeliveryStatus ?? "pending"}</span>
                      <button disabled={isBusy} onClick={() => resendOrder(order.id)}>Resend</button>
                    </div>
                  ))}
                </div>
              </section>}

              {detail && <section className="panel">
                <div className="panel-header"><h2>Tickets</h2><span>{tickets.length} recent</span></div>
                <div className="table-shell management-table">
                  <div className="table-row ticket-row table-head">
                    <span>Ticket</span><span>Tier</span><span>Buyer</span><span>Status</span><span>Delivery</span><span>Action</span>
                  </div>
                  {tickets.map((ticket) => (
                    <div className="table-row ticket-row" key={ticket.id}>
                      <button className="text-link" onClick={() => navigate(`/t/${ticket.ticketCode}`)}>{ticket.ticketCode}</button>
                      <span>{ticket.ticketTierName ?? "Tier"}</span>
                      <span>{ticket.buyerName ?? ticket.buyerEmail ?? "Buyer"}</span>
                      <span>{ticket.checkedIn ? "checked in" : ticket.status}</span>
                      <span>{ticket.deliveryStatus ?? "pending"}</span>
                      <button disabled={isBusy} onClick={() => resendTicket(ticket.id)}>Resend</button>
                    </div>
                  ))}
                </div>
              </section>}
            </>
          )}
        </div>
      )}
      {toast && <Toast toast={toast} onDismiss={() => setToast(undefined)} />}
    </section>
  );
}

function TierValidationMessages({ messages }: { messages: string[] }) {
  if (messages.length === 0) return null;
  return (
    <div className="organizer-inline-validation">
      {messages.map((issue) => (
        <p key={issue}>{issue}</p>
      ))}
    </div>
  );
}

export function TierAccordionCard({
  tier,
  form,
  isExpanded,
  timeZone,
  validationMessages,
  isBusy,
  cannotDelete,
  onToggle,
  onChange,
  onSave,
  onDelete
}: {
  tier: TicketTier;
  form: TierFormState;
  isExpanded: boolean;
  timeZone: string;
  validationMessages: string[];
  isBusy: boolean;
  cannotDelete: boolean;
  onToggle: () => void;
  onChange: (value: TierFormState) => void;
  onSave: () => void;
  onDelete: () => void;
}) {
  const contentId = `ticket-tier-${tier.id}`;
  const isScheduledClosed = Boolean(tier.saleEndsAt && Date.parse(tier.saleEndsAt) <= Date.now());
  const manualStatus = tier.status === "hidden" ? "Hidden" : tier.status === "paused" ? "Sales paused" : tier.status === "sold_out" ? "Sold out" : undefined;
  const statusLabel = isScheduledClosed ? "Closed" : manualStatus ?? humanizeSalesStatus(tier.salesStatus);
  const statusTone = statusLabel === "On sale" ? "success" : statusLabel === "Hidden" ? "info" : "warning";

  return (
    <article className={`tier-accordion-card${isExpanded ? " tier-accordion-card--expanded" : ""}`}>
      <button
        className="tier-accordion__header"
        id={`${contentId}-toggle`}
        type="button"
        aria-expanded={isExpanded}
        aria-controls={contentId}
        onClick={onToggle}
      >
        <span className="tier-accordion__chevron" aria-hidden="true">▸</span>
        <span className="tier-accordion__summary">
          <span className="tier-accordion__title-row">
            <strong>{tier.name}</strong>
            <strong>{currency.format(tier.priceCents / 100)}</strong>
          </span>
          <span className="tier-accordion__metadata">
            <StatusBadge label={statusLabel} tone={statusTone} />
            {tier.saleEndsAt && <span>{isScheduledClosed ? "Closed" : "Closes"} {formatZonedDateTime(tier.saleEndsAt, timeZone)}</span>}
            {tier.inventoryLimit !== undefined && <span>Inventory: {tier.inventoryLimit}</span>}
          </span>
        </span>
      </button>
      {isExpanded && (
        <div className="tier-accordion__content" id={contentId} role="region" aria-labelledby={`${contentId}-toggle`}>
          <p className="tier-accordion__unsaved-note">Changes are saved only when you select Save Changes. Opening another tier discards unsaved edits.</p>
          <TierFields value={form} onChange={onChange} timeZone={timeZone} />
          <TierValidationMessages messages={validationMessages} />
          <div className="button-row">
            <button className="primary-action" type="button" disabled={isBusy} onClick={onSave}>Save Changes</button>
            <button className="secondary-action" type="button" disabled={isBusy || cannotDelete} onClick={onDelete}>Delete Tier</button>
          </div>
        </div>
      )}
    </article>
  );
}

function TierFields({ value, onChange, timeZone }: { value: TierFormState; onChange: (value: TierFormState) => void; timeZone: string }) {
  const checkoutUrlError = validateStripeCheckoutLink(value.checkoutUrl ?? "");

  return (
    <div className="form-grid compact-form organizer-tier-fields">
      <FormField label="Ticket name">
        <input value={value.name} onChange={(e) => onChange({ ...value, name: e.target.value })} />
      </FormField>
      <FormField label="Price" hint="Enter normal dollar amounts like 8.00. AnTix stores cents behind the scenes.">
        <div className="currency-input">
          <span>$</span>
          <input
            inputMode="decimal"
            value={formatPriceInput(value.priceCents)}
            onChange={(e) => onChange({ ...value, priceCents: parsePriceInput(e.target.value) })}
          />
        </div>
      </FormField>
      <FormField label="Currency">
        <input value={value.currency ?? "USD"} onChange={(e) => onChange({ ...value, currency: e.target.value.toUpperCase() })} />
      </FormField>
      <FormField label="Stripe Checkout Link" hint="Optional reference link only. Public buyers now use the AnTix-managed checkout API.">
        <input value={value.checkoutUrl ?? ""} onChange={(e) => onChange({ ...value, checkoutUrl: e.target.value })} />
      </FormField>
      <FormField label="Inventory limit">
        <input type="number" value={value.inventoryLimit ?? ""} onChange={(e) => onChange({ ...value, inventoryLimit: e.target.value ? Number(e.target.value) : null })} />
      </FormField>
      <FormField label="Per-order limit">
        <input type="number" min="1" value={value.perOrderLimit ?? ""} onChange={(e) => onChange({ ...value, perOrderLimit: e.target.value ? Number(e.target.value) : null })} />
      </FormField>
      <FormField label="Display order">
        <input type="number" value={value.sortOrder ?? 0} onChange={(e) => onChange({ ...value, sortOrder: Number(e.target.value) })} />
      </FormField>
      <FormField label="Sales start">
        <input
          type="datetime-local"
          value={toDateTimeLocal(value.saleStartsAt)}
          onChange={(e) => onChange({ ...value, saleStartsAt: fromDateTimeLocal(e.target.value) })}
        />
      </FormField>
      <FormField label="Tier status" hint="Hide a tier from organizer and public listings without deleting it.">
        <select value={value.status ?? "active"} onChange={(e) => onChange({ ...value, status: e.target.value as TicketTierFormInput["status"] })}>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="sold_out">Sold out</option>
          <option value="hidden">Hidden</option>
        </select>
      </FormField>
      <FormField label="Tier sales status" hint="This is how the row appears publicly.">
        <select value={value.salesStatus ?? "on_sale"} onChange={(e) => onChange({ ...value, salesStatus: e.target.value as TicketTierFormInput["salesStatus"] })}>
          <option value="on_sale">On sale</option>
          <option value="sold_out">Sold out</option>
          <option value="sales_paused">Sales paused</option>
        </select>
      </FormField>
      <FormField label="Scheduled close" hint={"Time shown in " + timeZone + "."}>
        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={value.closeOnDate}
            onChange={(event) => onChange({
              ...value,
              closeOnDate: event.target.checked,
              saleEndsAt: event.target.checked ? value.saleEndsAt ?? null : null
            })}
          />
          Close tier on a date
        </label>
      </FormField>
      {value.closeOnDate && (
        <>
          <FormField label="Close date">
            <input
              type="date"
              value={value.closeDate}
              onChange={(event) => {
                const closeDate = event.target.value;
                onChange({ ...value, closeDate, saleEndsAt: zonedDateTimeToIso(closeDate, value.closeTime, timeZone) ?? null });
              }}
            />
          </FormField>
          <FormField label="Close time">
            <input
              type="time"
              value={value.closeTime}
              onChange={(event) => {
                const closeTime = event.target.value;
                onChange({ ...value, closeTime, saleEndsAt: zonedDateTimeToIso(value.closeDate, closeTime, timeZone) ?? null });
              }}
            />
          </FormField>
        </>
      )}
      <FormField label="Description" wide>
        <input value={value.description ?? ""} onChange={(e) => onChange({ ...value, description: e.target.value })} />
      </FormField>
      {checkoutUrlError && <p className="form-error organizer-field-error">{checkoutUrlError}</p>}
      <div className="organizer-tier-preview">
        <span className="organizer-section-label">Public preview</span>
        <div className="tier-purchase-row">
          <div className="tier-purchase-row__copy">
            <strong>{value.name || "Ticket tier"}</strong>
            <span>{value.priceCents > 0 ? currency.format(value.priceCents / 100) : formatMoney(0, value.currency ?? "USD")}</span>
          </div>
          <span className="tier-purchase-row__button tier-purchase-row__button-disabled" aria-disabled="true">
            {humanizeSalesStatus(value.salesStatus ?? "on_sale")}
          </span>
        </div>
      </div>
    </div>
  );
}

function PromoFields({ value, onChange }: { value: PromoCodeFormInput; onChange: (value: PromoCodeFormInput) => void }) {
  return (
    <div className="form-grid compact-form">
      <label>Code<input value={value.code} onChange={(e) => onChange({ ...value, code: e.target.value })} /></label>
      <label>Type<select value={value.discountType} onChange={(e) => onChange({ ...value, discountType: e.target.value as "percent" | "fixed" })}><option value="percent">percent</option><option value="fixed">fixed cents</option></select></label>
      <label>Value<input type="number" value={value.discountValue} onChange={(e) => onChange({ ...value, discountValue: Number(e.target.value) })} /></label>
      <label>Max uses<input type="number" value={value.maxUses ?? ""} onChange={(e) => onChange({ ...value, maxUses: e.target.value ? Number(e.target.value) : null })} /></label>
      <label>Starts at<input type="datetime-local" value={toDateTimeLocal(value.startsAt)} onChange={(e) => onChange({ ...value, startsAt: fromDateTimeLocal(e.target.value) })} /></label>
      <label>Ends at<input type="datetime-local" value={toDateTimeLocal(value.endsAt)} onChange={(e) => onChange({ ...value, endsAt: fromDateTimeLocal(e.target.value) })} /></label>
      <label className="checkbox-field"><input type="checkbox" checked={value.isActive !== false} onChange={(e) => onChange({ ...value, isActive: e.target.checked })} /> Active</label>
    </div>
  );
}

export function PromoAccordionCard({ promo, form, isExpanded, isBusy, onToggle, onChange, onSave, onCancel, onDeactivate, onReactivate }: {
  promo: PromoCode;
  form: PromoCodeFormInput;
  isExpanded: boolean;
  isBusy: boolean;
  onToggle: () => void;
  onChange: (value: PromoCodeFormInput) => void;
  onSave: () => void;
  onCancel: () => void;
  onDeactivate: () => void;
  onReactivate: () => void;
}) {
  const availability = promoAvailabilityLabel(promo);
  const discount = promo.discountType === "percent" ? `${promo.discountValue}% off` : `${currency.format(promo.discountValue / 100)} off`;
  const usage = promo.maxRedemptions === undefined ? `${promo.redeemedCount} uses` : `${promo.redeemedCount} / ${promo.maxRedemptions} uses`;
  return <article className="mini-card">
    <strong>{promo.code}</strong>
    <span>{discount}</span>
    <div className="button-row"><StatusBadge label={promo.isActive ? "Active" : "Inactive"} tone={promo.isActive ? "success" : "neutral"} /><StatusBadge label={availability} tone={availability === "Available" ? "success" : "warning"} /><span>{usage}</span></div>
    <div className="button-row">
      <button type="button" aria-expanded={isExpanded} onClick={onToggle} disabled={isBusy}>Edit</button>
      {promo.isActive ? <button type="button" onClick={onDeactivate} disabled={isBusy}>Deactivate</button> : <button type="button" onClick={onReactivate} disabled={isBusy}>Reactivate</button>}
    </div>
    {isExpanded && <div className="stack-list">
      <PromoFields value={form} onChange={onChange} />
      <div className="button-row"><button type="button" className="primary-action" onClick={onSave} disabled={isBusy}>Save Changes</button><button type="button" onClick={onCancel} disabled={isBusy}>Cancel</button></div>
    </div>}
  </article>;
}
